import { inject, Injectable } from '@angular/core'

import { NUMBER_MINUS_ONE } from '../../../constants/constant.numbers'
import { IDisplayTableModel } from '../../models/display-table-model'
import { FilterByModel } from '../../models/filterby-model'
import { DisplayTableView } from '../../shared/display-table-view'
import { FilterModel } from '../../shared/filter-model'
import { AbstractFilterProcess } from './abstract-filter-process'
import { DisplayContentViewGenerator } from './display-content-view-generator'
import { FilterHandlerFactory } from './filter-handler-factory'
import { FundClassificationProcess } from './fund-classification-process'
import { AbstractFilterHandlerResultModel } from './models/abstract-filter-handler-result-model'
import { AggregateHandlerResultModel } from './models/aggregate-handler-result-model'
import { FilterHandlerContext } from './models/filter-handler-context'
import { FilterHandlerResultType } from './models/filter-handler-result-type.enum'
import { FilterState } from './models/filter-state'
import { FundCategoryModel } from './models/fund-category-model'
import { IFilterState } from './models/ifilter-state'
import { UnmodifiableFilterStateWrapper } from './models/unmodifiable-filter-state-wrapper'
import { ConsolidatedCustomRules } from './rules/consolidated-custom-rules'
import { FundRuleFactory } from './rules/fund-rule-factory'
import { FundRuleName } from './rules/fund-rule-name'
import { IFilterHandler } from './rules/ifilter-handler'
import { IFundRule } from './rules/ifund-rule'

@Injectable({
  providedIn: 'root',
})
export class FilterProcessImpl extends AbstractFilterProcess {
  private ruleMap: Map<string, FundRuleName> = new Map<string, FundRuleName>()
  private readonly unmodifiableFilterStateWrapper: UnmodifiableFilterStateWrapper
  private searchText = ''
  private availableCategoryLvl1Names: FilterByModel
  private selectedFilterCategory = []

  filterModel: FilterModel

  constructor(
    private readonly filterState: FilterState,
    private readonly filterHandlerFactory: FilterHandlerFactory,
    private readonly fundClassificationProcess: FundClassificationProcess,
    private readonly fundRuleFactory: FundRuleFactory,
  ) {
    super()
    this.filterModel = inject(FilterModel)
    this.unmodifiableFilterStateWrapper = new UnmodifiableFilterStateWrapper()
    this.unmodifiableFilterStateWrapper.setFilterState(this.filterState)
    this.generateFundRuleNameMap()
  }

  protected getFilterState(): IFilterState {
    return <IFilterState>this.unmodifiableFilterStateWrapper
  }

  protected generateView(
    categoryModelList: FundCategoryModel[],
  ): DisplayTableView {
    const viewGenerator: DisplayContentViewGenerator =
      new DisplayContentViewGenerator(categoryModelList)
    return viewGenerator.generateView(
      this.filterModel.isMultipleSubAssetSelected(),
      this.selectedFilterCategory,
    )
  }

  private generateFundRuleNameMap() {
    Object.keys(FundRuleName).map((key) => {
      this.ruleMap.set(FundRuleName[key].toString(), FundRuleName[key])
    })
  }

  protected executeFilterHandlers(
    filterSelectionsAry: string[],
  ): AggregateHandlerResultModel {
    // Get filter handler context
    const handlerContext: FilterHandlerContext = this.getFilterHandlerContext()
    const ruleCollection: IFundRule[] = []

    let filterHandlerResult: AbstractFilterHandlerResultModel,
      filterHandler: IFilterHandler
    const fundGroupList: FundCategoryModel[] = []

    // Iterate over the filter selection and execute specific filter handlers
    filterSelectionsAry.forEach((userSelection) => {
      // Obtain the filter handler instance
      filterHandler = this.filterHandlerFactory.getInstance(
        this.ruleMap.get(userSelection),
      )
      if (filterHandler) {
        filterHandlerResult = filterHandler.handleFilterSelection(
          this.ruleMap.get(userSelection),
          handlerContext,
        )

        // Accumulating the handlers execution results
        if (
          filterHandlerResult.getFilterHandlerResultType() ===
          FilterHandlerResultType.ResultTypeRule
        ) {
          // Handler result is a RULE.
          ruleCollection.push(filterHandlerResult.getAppliedFundRule())
        } else {
          // Handler result is of type Fund Collections
          filterHandlerResult
            .getFundGroupList()
            .forEach((tempFundCategoryModel: FundCategoryModel) => {
              if (
                tempFundCategoryModel &&
                tempFundCategoryModel.fundDtlAry &&
                tempFundCategoryModel.fundDtlAry.length > 0
              ) {
                fundGroupList.push(tempFundCategoryModel)
              }
            })
        }
      }
      // End If-handler
    })
    return new AggregateHandlerResultModel(fundGroupList, ruleCollection)
  }

  private getFilterHandlerContext(): FilterHandlerContext {
    return new FilterHandlerContext(
      this.fundClassificationProcess,
      this.fundRuleFactory,
    )
  }

  protected covertDisplayTableContentToMap(
    displayTableContentAry: IDisplayTableModel[],
  ): Map<string, IDisplayTableModel> {
    const tableContentMap: Map<string, IDisplayTableModel> = new Map<
      string,
      IDisplayTableModel
    >()
    displayTableContentAry.forEach((model: IDisplayTableModel) => {
      if (!this.searchText || this.isSearchTextMatch(model, this.searchText)) {
        tableContentMap.set(model.getFundId(), model)
      }
    })
    return tableContentMap
  }

  private isSearchTextMatch(product: any, searchTxt: string): boolean {
    return (
      product.fundID.toUpperCase().includes(searchTxt.toUpperCase()) ||
      (product.fundName &&
        product.fundName.toUpperCase().includes(searchTxt.toUpperCase())) ||
      (product.symbol &&
        product.symbol.toUpperCase().includes(searchTxt.toUpperCase()))
    )
  }

  protected applyAggregateRules(
    aggregateHandlerResultModel: AggregateHandlerResultModel,
    displayTableContentMap: Map<string, IDisplayTableModel>,
  ): FundCategoryModel[] {
    const consolidatedCustomRules: ConsolidatedCustomRules =
        new ConsolidatedCustomRules(aggregateHandlerResultModel.getRuleList()),
      isRuleListEmpty: boolean = aggregateHandlerResultModel.isRuleListEmpty(),
      fundCategoryModelResult: FundCategoryModel[] = []

    let fundCategoryModelUpdated: FundCategoryModel
    this.selectedFilterCategory = []
    // Iteration over getFundCategoryList
    aggregateHandlerResultModel
      .getFundCategoryList()
      .forEach((fundCategoryModelElem: FundCategoryModel) => {
        if (fundCategoryModelElem) {
          fundCategoryModelUpdated = new FundCategoryModel(
            fundCategoryModelElem.categoryLvl1Name,
            fundCategoryModelElem.categoryLvl2Name,
            fundCategoryModelElem.categoryLvl3Name,
          )
          // Iteration over  fund list
          fundCategoryModelElem.fundDtlAry.forEach((fund) => {
            if (isRuleListEmpty) {
              this.pushToCollection(
                displayTableContentMap.get(fund.portId),
                fundCategoryModelUpdated,
              )
            } else {
              if (consolidatedCustomRules.isRuleApplicable(fund)) {
                this.pushToCollection(
                  displayTableContentMap.get(fund.portId),
                  fundCategoryModelUpdated,
                )
              }
            }
          })

          // Add update fundCategoryModel to collection
          if (fundCategoryModelUpdated.fundDtlAry.length > 0) {
            fundCategoryModelResult.push(fundCategoryModelUpdated)
          } else {
            // Setting the selected filter category array if there is no funds available under the category
            this.setSelectedFilterCategory(
              fundCategoryModelUpdated.categoryLvl3Name,
            )
          }
        }
      })

    return fundCategoryModelResult
  }

  private pushToCollection(fund: any, fundGroupListTemp: FundCategoryModel) {
    if (fund) {
      fundGroupListTemp.fundDtlAry.push(fund)
    }
  }

  private setSelectedFilterCategory(filterCategory: string) {
    if (
      this.selectedFilterCategory.indexOf(filterCategory) === NUMBER_MINUS_ONE
    ) {
      this.selectedFilterCategory.push(filterCategory)
    }
  }

  setSearchText(searchText: string) {
    this.searchText = searchText
  }

  protected setAvailableCategoryLvl1Names(
    fundCategoryModelList: FundCategoryModel[],
  ) {
    this.availableCategoryLvl1Names = {
      Eq: false,
      Fi: false,
      MulAsset: false,
      CashEqv: false,
      Div: false,
      Esg: false,
      FactorDriven: false,
      SectSpl: false,
    }
    fundCategoryModelList.forEach((fundCategoryModel) => {
      if (fundCategoryModel.categoryLvl1Name) {
        this.availableCategoryLvl1Names[fundCategoryModel.categoryLvl1Name] =
          true
      }
    })
  }

  getAvailableCategoryLvl1Names() {
    return this.availableCategoryLvl1Names
  }
}
